import {
    type FC,
    useCallback,
    useRef,
    useState,
    useMemo,
    memo,
} from "react";

import { LetsStartPopUp } from "./gameComponents/LetsStartPopUp";
import { ResultTable } from "./gameComponents/ResultTable";
import { Slide } from "./gameComponents/Slide";
import { ThankYouScreen } from "./gameComponents/ThankYouScreen";
import { productsEN, topProductsEN } from "./translations/products";
import type { Language, Product, Result, ResultWithCorrectValue } from "./types";
import { QuitGamePopUp } from "./gameComponents/QuitGamePopup";

import './GameContainer.css';
import { Toast } from "./gameComponents/Toast";

interface GameContainerProps {
    onBackClick: ()=> void,
    setResultsVisibleCallback: (visible: boolean)=> void,
    language: Language,
}

const NUMBER_OF_SLIDES = 30;

export const GameContainer: FC <GameContainerProps> = memo(({
    onBackClick,
    language,
    setResultsVisibleCallback,
}) => {
    const topProducts = useRef<Product>(topProductsEN);
    const nextProducts = useRef<Product>(productsEN);
    const productList = useRef<string []>(
        Object.keys(topProducts.current).sort(() => Math.random() - 0.5)
    );;
    const [nextProductList, setNextProductList] = useState<string[]>(
        Object.keys(nextProducts.current).sort(() => Math.random() - 0.5).slice(0, NUMBER_OF_SLIDES)
    );
    const [currentProductIndex, setCurrentProductIndex] = useState(0);
    const [resultsVisible, setResultsVisible] = useState(false);
    const [thankYouScreenVisible, setThankYouScreenVisible] = useState(false);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [listOfResults, setListOfResults] = useState<ResultWithCorrectValue[]>([]);
    const [quitGamePopUpVisible, setQuitGamePopUpVisible] = useState(false);
    const [nextProductsUsed, setNextProductsUsed] = useState(false);
    
    const updateProduct = useCallback(() => {
        if(currentProductIndex === productList.current.length - 1) {
            setThankYouScreenVisible(true);
            setCurrentProductIndex(0);
        } else {
            setCurrentProductIndex(currentProductIndex + 1);
        }
    },[currentProductIndex, productList]);

    const handleShowResults = useCallback(() => {
        setThankYouScreenVisible(false);
        setResultsVisible(true);
        setResultsVisibleCallback(true);
    },[setResultsVisibleCallback]);

    const handleStartGame = useCallback(() => {
        setIsGameStarted(true);
    },[]);

    const hideQuitGamePopUp = useCallback(() => {
        setQuitGamePopUpVisible(false);
    },[setQuitGamePopUpVisible]);

    const handleCloseClick = useCallback(() => {
        setQuitGamePopUpVisible(true);
    },[setQuitGamePopUpVisible]);

    const handleQuitGame = useCallback(() => {
        setIsGameStarted(false);
        setThankYouScreenVisible(true);
        hideQuitGamePopUp();
    },[setIsGameStarted,setThankYouScreenVisible, hideQuitGamePopUp]);

    const handlePlayAgainClick = useCallback(() => {
        setResultsVisible(false);
        setListOfResults([]);
        setCurrentProductIndex(0);
        setIsGameStarted(true);
        setNextProductsUsed(true);
        setNextProductList(Object.keys(nextProducts.current).sort(() => Math.random() - 0.5).slice(0, NUMBER_OF_SLIDES));
        setResultsVisibleCallback(false);
    },[setResultsVisible, setListOfResults, setCurrentProductIndex, setResultsVisibleCallback]);

    const handleUpdateListOfResults = useCallback((result: Result) => {
        const currentProductValue = nextProductsUsed
            ? nextProducts.current[result.product]
            : topProducts.current[result.product];
        const isCorrect = currentProductValue === result.color;

        setListOfResults([
            ...listOfResults,
            {
                product: result.product,
                color: result.color,
                isCorrect: isCorrect,
                correctAnswer: currentProductValue,
            },
        ]);
    },[listOfResults, nextProductsUsed]);
    
    const getCorrectAnswersCount = useMemo((): number => {
        const listOfCorrectAnswers = listOfResults.filter(a => a.isCorrect);
        if (listOfCorrectAnswers) {
            return listOfCorrectAnswers.length;
        }
        return 0;
    },[listOfResults]);

    const renderQuitGamePopup = useMemo(() => {
        if (quitGamePopUpVisible) {
            return (
                <>
                    <div className="quitGamePopupBackgroundBlur"></div>
                    <QuitGamePopUp
                        onContinuClick={hideQuitGamePopUp}
                        onQuitClick={handleQuitGame}
                    />
                </>
            )
        }
    },[quitGamePopUpVisible, hideQuitGamePopUp, handleQuitGame]);

    if (thankYouScreenVisible) {
        return <ThankYouScreen
            correctAnswers={getCorrectAnswersCount}
            answersTotal={listOfResults.length}
            onShowResultsClick={handleShowResults}
        />
    }

    if (resultsVisible) {
        return <ResultTable
            language={language}
            results={listOfResults}
            onPlayAgainClick={handlePlayAgainClick}
        />
    }

    if (!isGameStarted) {
        return (
            <LetsStartPopUp 
                onBackClick={onBackClick}
                onStartClick={handleStartGame}
            />
        );
    }
    return (
        <div className="gameContainer">
            <Toast
                questionsLeft={NUMBER_OF_SLIDES - currentProductIndex}
                totalQuestions={NUMBER_OF_SLIDES}
                currentProductNumber={currentProductIndex + 1}
            />
            <Slide
                language={language}
                product={ nextProductsUsed
                    ? nextProductList[currentProductIndex]
                    : productList.current[currentProductIndex]
                }
                updateList={handleUpdateListOfResults}
                changeSlide={updateProduct}
                hasImage={!nextProductsUsed}
            />
            <div className="closeButton" onClick={handleCloseClick}>
                <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
                    <path d="M0 0h24v24H0z" fill="none"/>
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
            </div>
            
            <div className="progres">
                {currentProductIndex + 1}/{productList.current.length}
            </div>
            
            {renderQuitGamePopup}
        </div>
    );
});
